import React, { useCallback, useEffect, useState } from 'react'
import { FolderView } from './FolderView'
import { ModalDocument } from './Modals/modalDocument'
import DocManagementService from '../../../../../API/Documents/DocumentManagement'
import Loading from '../../../../../Components/Loading/Loading'

function getFolderPathById(folderId, folders) {
	const path = []
	let current = folders.find(f => f.id === folderId)
	while (current) {
		path.unshift(current)
		current = folders.find(f => f.id === current.parentId)
	}
	return path
}

function useFolderNavigation(folders) {
	const [folderId, setFolderId] = useState(null)
	const individualRoot = folders.find(f => f.private === true && f.parentId === null)
	const currentFolder = folderId ? folders.find(f => f.id === folderId) : null

	const handleOpen = id => setFolderId(id)

	const handleBreadcrumbClick = item => {
		if (item.name === 'Início') {
			setFolderId(null)
		} else if (item.id === individualRoot?.id) {
			setFolderId(individualRoot.id)
		} else {
			setFolderId(item.id)
		}
	}

	return {
		folderId,
		currentFolder,
		individualRoot,
		handleOpen,
		handleBreadcrumbClick,
	}
}

function useFolderContent(currentFolder, individualRoot, folders) {
	if (currentFolder?.id === individualRoot?.id) {
		return {
			folders: folders.filter(f => f.private && f.parentId === individualRoot.id),
			documents: [],
		}
	}
	return {
		folders: folders.filter(f => f.parentId === (currentFolder?.id ?? null)),
		documents: currentFolder?.folderDocuments || currentFolder?.documents || [],
	}
}

function useBreadcrumb(folderId, folders, individualRoot) {
	const pathChain = folderId ? getFolderPathById(folderId, folders) : []
	const breadcrumb = [{ id: null, name: 'Início' }]
	if (pathChain.length) {
		if (pathChain[0]?.id === individualRoot?.id) {
			breadcrumb.push({
				id: individualRoot.id,
				name: individualRoot.private ? 'Individual' : 'Comum',
			})
			breadcrumb.push(...pathChain.slice(1).map(folder => ({ id: folder.id, name: folder.name })))
		} else {
			breadcrumb.push(...pathChain.map(folder => ({ id: folder.id, name: folder.name })))
		}
	}
	return breadcrumb
}

export const Breadcrumb = ({ items, onClick }) => (
	<nav className='tw-flex tw-items-center tw-gap-2 tw-text-base tw-text-gray-500 tw-mt-4'>
		{items.map((item, i) => {
			const isLast = i === items.length - 1
			return (
				<span key={item.id || item.name} className='tw-flex tw-items-center tw-gap-2'>
					{i > 0 && <span>/</span>}
					<span
						className={!isLast ? 'tw-text-purple-500 tw-cursor-pointer hover:tw-text-indigo-600' : ''}
						onClick={!isLast ? () => onClick(item) : undefined}
					>
						{item.name}
					</span>
				</span>
			)
		})}
	</nav>
)

export const DocManagerEmployee = () => {
	const user = JSON.parse(localStorage.getItem('user'))
	const employeeId = user?.id

	const [folders, setFolders] = useState([])
	const [loading, setLoading] = useState(false)
	const [isModalDocumentOpen, setIsModalDocumentOpen] = useState(false)

	const { folderId, currentFolder, individualRoot, handleOpen, handleBreadcrumbClick } = useFolderNavigation(folders)

	const { folders: foldersToShow, documents: documentsToShow } = useFolderContent(currentFolder, individualRoot, folders)

	const breadcrumb = useBreadcrumb(folderId, folders, individualRoot)

	const loadFolders = useCallback(async () => {
		setLoading(true)
		try {
			const respFolders = await DocManagementService.findFoldersEmployee(employeeId)
			setFolders(respFolders)
		} catch (error) {
			console.error('Erro ao carregar pastas:', error)
		} finally {
			setLoading(false)
		}
	}, [employeeId])

	useEffect(() => {
		loadFolders()
	}, [loadFolders])

	const formatDate = useCallback(timestamp => {
		const date = new Date(timestamp)
		return date.toLocaleDateString('pt-BR', {
			day: '2-digit',
			month: 'long',
			year: 'numeric',
		})
	}, [])

	// Document actions
	const handleDocumentSubmit = useCallback(
		async (data, files) => {
			setLoading(true)
			try {
				const uploadRoutes = await DocManagementService.uploadDocumentsEmp(data)
				await Promise.all(
					uploadRoutes
						.map((route, i) => ({ ...route, file: files[i] }))
						.filter(({ url, file }) => !!url && !!file)
						.map(({ url, file }) =>
							fetch(url, {
								method: 'PUT',
								body: file,
								headers: {
									'Content-Type': file.type,
								},
							})
						)
				)

				await loadFolders()
				setIsModalDocumentOpen(false)
			} catch (error) {
				console.error('Erro ao subir documentos:', error)
			} finally {
				setLoading(false)
			}
		},
		[loadFolders]
	)

	const handleDeleteDocument = useCallback(
		async documentId => {
			setLoading(true)
			try {
				await DocManagementService.deleteDocument(documentId)
				await loadFolders()
			} catch (error) {
				console.error('Erro ao deletar documento:', error)
			} finally {
				setLoading(false)
			}
		},
		[loadFolders]
	)

	const handleMoveDocument = useCallback(
		async (documentId, newFolderId) => {
			setLoading(true)
			try {
				await DocManagementService.moveDocument(documentId, { folderId: newFolderId })
				await loadFolders()
			} catch (error) {
				console.error('Erro ao mover documento:', error)
			} finally {
				setLoading(false)
			}
		},
		[loadFolders]
	)

	return (
		<>
			<div className='tw-flex tw-items-start tw-justify-between tw-mb-4 tw-pr-10'>
				<div>
					<h3 className='fw-bold tw-mb-4'>Meus Documentos</h3>
					<div>Aquí puedes acceder y enviar documentos a la empresa o a tu carpeta personal.</div>
				</div>
				<div>
					<div
						className='tw-bg-purple-500 tw-text-white tw-px-4 tw-py-2 hover:tw-bg-purple-700 tw-cursor-pointer tw-rounded-lg tw-whitespace-nowrap'
						onClick={() => setIsModalDocumentOpen(true)}
					>
						Subir Novo Documento
					</div>
				</div>
			</div>

			<div className='tw-text-sm tw-text-gray-500 tw-mb-2'>
				Los documentos de la carpeta Común y sus subcarpetas pueden ser accedidos por varios colaboradores al mismo tiempo. Ejemplo: Código de
				Conducta.
			</div>
			<div className='tw-text-sm tw-text-gray-500 tw-mb-6'>
				Los documentos de la carpeta Individual solo podrán ser accedidos por el colaborador correspondiente. Ejemplo: Recibo de salario.
			</div>

			{loading ? (
				<Loading />
			) : (
				<>
					<Breadcrumb items={breadcrumb} onClick={handleBreadcrumbClick} />

					<FolderView
						folders={foldersToShow}
						documents={documentsToShow}
						onOpen={handleOpen}
						formatDate={formatDate}
						allowClick={true}
						currentFolder={currentFolder}
						onDeleteDocument={handleDeleteDocument}
						onMoveDocument={handleMoveDocument}
						employeeView={true}
						modalFolders={folders}
					/>

					<ModalDocument
						isOpen={isModalDocumentOpen}
						folders={folders}
						onClose={() => setIsModalDocumentOpen(false)}
						onSubmit={handleDocumentSubmit}
						employeeView={true}
					/>
				</>
			)}
		</>
	)
}
